import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";
import { WeaveLogo } from "@/components/WeaveLogo";
import { FadeUp, Overline } from "@/components/Motion";
import { createCheckout } from "@/lib/api";

const OFFER = {
  overline: "Personal Clarity complete",
  title: "Complete your Academy year.",
  copy: (
    <>
      You began with Personal Clarity. If you are ready to continue into the Business and External
      Clarity sections, the remaining{" "}
      <strong className="text-weave-ink font-medium">£2,501</strong> completes your place —
      bringing the total to £3,000.
    </>
  ),
  emailLabel: "The email you used for Personal Clarity",
  plan: "clarity_balance",
  button: "Complete the year — £2,501",
};

export default function Balance() {
  const config = OFFER;
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error(`Add ${config.emailLabel.toLowerCase()}.`);
      return;
    }
    setBusy(true);
    try {
      const origin_url = window.location.origin;
      const res = await createCheckout({
        origin_url,
        email,
        plan: config.plan,
      });
      window.location.assign(res.checkout_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open checkout — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-weave-sand" data-testid="clarity-balance-page">
      <header className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="group inline-flex items-center gap-2 text-weave-slate hover:text-weave-ink transition-colors duration-300 text-sm font-medium" data-testid="balance-back-button">
          <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" /> Back
        </button>
        <WeaveLogo />
      </header>
      <main className="max-w-2xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <FadeUp className="bg-weave-cream border border-weave-line rounded-2xl p-8 md:p-12">
          <Overline>{config.overline}</Overline>
          <h1 className="mt-4 text-3xl md:text-4xl font-light tracking-tight text-weave-ink">
            {config.title}
          </h1>
          <p className="mt-5 text-lg text-weave-slate leading-relaxed">{config.copy}</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="balance-form">
            <input
              type="email"
              aria-label={config.emailLabel}
              placeholder={config.emailLabel}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="balance-email-input"
              className="w-full rounded-xl border border-weave-line bg-white px-4 py-3 text-sm text-weave-ink placeholder:text-weave-mute focus:border-weave-teal focus:outline-none transition-colors duration-200"
            />
            <button type="submit" disabled={busy} data-testid="balance-pay-button" className="w-full inline-flex items-center justify-center gap-2 bg-weave-teal text-white hover:bg-[#3f8f93] transition-colors duration-300 rounded-full px-8 py-4 font-medium disabled:opacity-60">
              <Lock size={15} />
              {busy ? "Opening secure checkout…" : config.button}
            </button>
          </form>
        </FadeUp>
      </main>
    </div>
  );
}
